<template>
    <layout-authenticated>
        <v-breadcrumbs :items="breadcrumb"></v-breadcrumbs>
        <h1 class="mb-3">{{ $t('approval.edit_bulk_order_no', {n: bulkOrderId}) }}</h1>
        <template v-if="bulkOrder !== null">
            <h2 class="grey--text mb-3">{{ $t('approval.shopping_carts_in_bulk_order') }}</h2>
            <edit-order-in-bulk-order
                v-for="(order, i) in orders"
                :key="i"
                :index="order.index"
                :userId="userId"
                :warehouseId="warehouseId"
                :total-number-of-orders="orders.length"
            />
            <h2 class="grey--text my-3">{{ $t('approval.order_message') }}</h2>
            <v-text-field
                v-model="$v.form.name.$model"
                :label="$t('shopping_cart.name')"
                :error-messages="nameErrors"
                outlined
                dense
                @input="$v.form.name.$touch()"
                @blur="$v.form.name.$touch()"
            />
            <v-textarea
                @input="onMessageChange"
                v-model="$v.form.message.$model"
                :label="$t('summary.message')"
                :error-messages="messageErrors"
                rows="3"
                outlined
                dense
            />
            <v-btn
                @click="onSave(false)"
                :disabled="!bulkOrderValid"
                :loading="isBusySave"
                color="primary"
                class="mt-3 mr-3"
            >
                <v-icon left>mdi-content-save-outline</v-icon>
                {{ $t('approval.save') }}
            </v-btn>
            <v-btn
                @click="onSave(true)"
                :disabled="!bulkOrderValid"
                :loading="isBusySave"
                color="warning"
                class="mt-3"
            >
                <v-icon left>mdi-checkbox-marked-circle-plus-outline</v-icon>
                {{ $t('approval.save_and_approve') }}
            </v-btn>
        </template>
    </layout-authenticated>
</template>

<script>
import LayoutAuthenticated from "@/layouts/LayoutAuthenticated";
import {API} from "@/api/api";
import EditOrderInBulkOrder from "@/components/approver/editBulkOrder/EditOrderInBulkOrder";
import SnackbarMixin from "@/mixins/SnackbarMixin";
import {required} from "vuelidate/lib/validators";
import {validationMixin} from "vuelidate";
import _ from "lodash";

export default {
    name: "EditBulkOrder",
    mixins: [SnackbarMixin, validationMixin],
    components: {EditOrderInBulkOrder, LayoutAuthenticated},
    data: () => ({
        // loading
        isBusyBulkOrder: false,
        isBusySave: false,
        // data
        bulkOrder: null,
        // form
        form: {
            name: null,
            message: null
        },
        // is bulk order valid
        valid: true
    }),
    validations: {
        form: {
            name: {required},
            message: {}
        }
    },
    computed: {
        userId() {
            return this.bulkOrder.user_id;
        },
        warehouseId() {
            return this.bulkOrder.warehouse_id;
        },
        bulkOrderId() {
            return this.$route.params.bulkOrderId;
        },
        breadcrumb() {
            return [
                {
                    text: this.$t('app.name'),
                    href: '/'
                },
                {
                    text: this.$t('approval.approval'),
                    href: '/approver/approval'
                },
                {
                    text: this.$t('approval.edit_bulk_order'),
                    href: '/approver/bulk-orders/' + this.bulkOrderId,
                    disabled: true
                }
            ];
        },
        bulkOrderValid() {
            return this.$store.getters["editBulkOrder/isValid"]({
                userId: this.userId,
                warehouseId: this.warehouseId
            })
        },
        orders() {
            const data = this.$store.getters["editBulkOrder/getData"]
            return data[this.userId][this.warehouseId];
        },
        productSets() {
            // aggregate all recipients from all orders
            let recipients = [];
            this.orders.forEach(order => {
                order.recipients.forEach(recipient => {
                    if (!recipients.find(r => r.address_id === recipient.address_id)) {
                        recipients.push(recipient);
                    }
                });
            });

            /**
             * key: {
             *   productSet: {...},
             *   recipients: [...]
             * }
             */
            let productSets = {};

            // get all products for given recipient
            recipients.forEach(recipient => {
                let productSet = {};
                this.orders.forEach(order => {
                    if (order.recipients.find(r => r.address_id === recipient.address_id)) {
                        order.order.forEach(product => {
                            if (product.external_id in productSet) {
                                productSet[product.external_id].quantity
                                    = parseInt(productSet[product.external_id].quantity) + parseInt(product.quantity);
                            } else {
                                productSet[product.external_id] = _.cloneDeep(product);
                            }
                            productSet[product.external_id].quantity = parseInt(productSet[product.external_id].quantity);
                        });
                    }
                });
                const hash = this.productSetHash(productSet);
                if (hash in productSets) {
                    if (!productSets[hash].recipients.find(r => r.address_id === recipient.address_id)) {
                        productSets[hash].recipients.push(recipient);
                    }
                } else {
                    productSets[hash] = {
                        productSet: productSet,
                        recipients: [recipient]
                    };
                }
            });

            return Object.values(productSets);
        },
        nameErrors() {
            const errors = [];
            if (!this.$v.form.name.$dirty) return errors;
            !this.$v.form.name.required && errors.push(this.$t('validation.name.required'));
            return errors;
        },
        messageErrors() {
            return [];
        }
    },
    methods: {
        fetchBulkOrder() {
            this.isBusyBulkOrder = true;
            API.get("/api/bulk-orders/" + this.bulkOrderId)
                .then(response => {
                    this.bulkOrder = response.data;
                    this.form.name = response.data.name;
                    this.form.message = response.data.orders[0]?.message ?? "";
                    if (this.bulkOrderId != this.$store.getters["editBulkOrder/getBulkOrderId"]) {
                        this.$store.commit("editBulkOrder/setBulkOrderId", response.data.id);
                        this.$store.commit("editBulkOrder/setData", JSON.parse(response.data.shopping_cart_data));
                    }
                    // init "is bulk order valid" watcher
                    this.$store.watch(() => this.$store.getters["editBulkOrder/isValid"]({
                        userId: this.userId,
                        warehouseId: this.warehouseId
                    }), (isValid) => {
                        this.valid = isValid;
                    });
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyBulkOrder = false;
                });
        },
        onMessageChange(message) {
            const data = this.$store.getters["editBulkOrder/getData"];
            for (let i = 0; i < data[this.userId][this.warehouseId].length; i++) {
                data[this.userId][this.warehouseId][i].message = message;
            }
            this.$store.commit("editBulkOrder/setData", data);
        },
        onSave(approve) {
            const data = {
                name: this.form.name,
                warehouse_id: this.warehouseId,
                shopping_cart_data: JSON.stringify(this.$store.getters["editBulkOrder/getData"]),
                orders: []
            };

            this.productSets.forEach(item => {
                const productSet = item.productSet;
                const recipients = item.recipients;

                recipients.forEach(recipient => {
                    data.orders.push({
                        // address & contact info
                        buyer_external_id: recipient.buyer_id,
                        address_external_id: recipient.address_id,
                        phone: recipient.phone,
                        email: recipient.email,
                        // warehouse
                        warehouse_id: this.warehouseId,
                        // ordered items
                        items: Object.values(productSet).map(product => ({
                            name: product.name,
                            external_id: product.external_id,
                            brand: product.brand,
                            model: product.model,
                            image_url: product.image_url,
                            quantity: product.quantity,
                        })),
                        // optional message
                        message: this.form.message
                    });
                });
            });

            this.isBusySave = true;
            API.put("/api/bulk-orders/" + this.bulkOrderId, data)
                .then(() => {
                    this.snackbarSuccess(this.$t("snackbar.bulk_order.updated"));
                    this.$store.commit("editBulkOrder/setData", {});
                    this.$store.commit("editBulkOrder/setBulkOrderId", null);
                    if (approve) {
                        this.$router.push({name: "bulk-order", params: {bulkOrderId: this.bulkOrderId}});
                    } else {
                        this.$router.push({name: "approval"});
                    }
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusySave = false;
                });
        },
        productSetHash(productSet) {
            let hash = Object.values(productSet)
                .map(product => "" + product.external_id + "-" + product.quantity)
                .sort()
                .join(";");
            return hash;
        }
    },
    created() {
        this.fetchBulkOrder();
    }
}
</script>
